import { useState } from 'react';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';
import { Button } from '@/core/components/ui/button';
import { Skeleton } from '@/core/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/core/components/ui/tooltip';
import { AlertTriangle, Image, Film, FileJson, FileImage, FileUp, Trash2, Eye, ExternalLink, Copy, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { copyToClipboard } from '@/core/utils/clipboard';
import { cn } from '@/core/lib/utils';
import {
  Asset,
  AssetType,
  APPLICATION_TYPE_LABELS,
  ASSET_TYPE_LABELS,
  formatFileSize,
  formatDate,
} from '../../types/asset-types';

interface AssetGridProps {
  assets: Asset[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onView: (asset: Asset) => void;
  onDelete: (asset: Asset) => void;
  onRetry: () => void;
}

function getAssetTypeIcon(assetType: AssetType) {
  switch (assetType) {
    case AssetType.Icon:
      return <FileImage className="h-4 w-4 text-blue-500" />;
    case AssetType.Image:
      return <Image className="h-4 w-4 text-green-500" />;
    case AssetType.Video:
      return <Film className="h-4 w-4 text-purple-500" />;
    case AssetType.Lottie:
      return <FileJson className="h-4 w-4 text-orange-500" />;
    case AssetType.File:
      return <FileUp className="h-4 w-4 text-gray-500" />;
  }
}

function AssetThumbnail({ asset }: { asset: Asset }) {
  const [imageError, setImageError] = useState(false);
  const canShowThumbnail = asset.assetType === AssetType.Icon || asset.assetType === AssetType.Image;

  const handleOpenPreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(asset.currentS3Url, '_blank');
  };

  if (!canShowThumbnail) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={handleOpenPreview}
              className="h-10 w-10 rounded bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              {getAssetTypeIcon(asset.assetType)}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-xs">
            <p className="font-medium">{asset.name}</p>
            <p className="text-xs text-gray-500">Click to open {ASSET_TYPE_LABELS[asset.assetType]}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (imageError) {
    return (
      <div className="h-10 w-10 rounded bg-gray-100 flex items-center justify-center">
        {getAssetTypeIcon(asset.assetType)}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleOpenPreview}
            className="h-10 w-10 rounded bg-gray-100 flex items-center justify-center overflow-hidden hover:ring-2 hover:ring-blue-400 transition-all"
          >
            <img
              src={asset.currentS3Url}
              alt={asset.name}
              className="max-h-full max-w-full object-contain"
              onError={() => setImageError(true)}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="p-1">
          <div className="space-y-1">
            <img
              src={asset.currentS3Url}
              alt={asset.name}
              className="max-w-[200px] max-h-[200px] object-contain rounded"
            />
            <p className="text-xs text-center text-gray-500">Click to open full size</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function AssetGrid({
  assets,
  isLoading,
  error,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onView,
  onDelete,
  onRetry,
}: AssetGridProps) {
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount > 0 ? currentPage * pageSize + 1 : 0;
  const endItem = Math.min((currentPage + 1) * pageSize, totalCount);

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 4;
    let startPage = Math.max(0, currentPage - 1);
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(0, endPage - maxPagesToShow + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="rounded-md border border-[#E8E8E8] overflow-hidden">
        <table className="w-full">
          <thead className={GRID_STYLES.header.background}>
            <tr>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-16')}></th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>Name</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Type</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-40')}>Application</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-20')}>Version</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Size</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-40')}>Modified</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-32')}>Modified By</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-32')}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {[...Array(5)].map((_, i) => (
              <tr key={i} className={cn(GRID_STYLES.row.default, `border-b ${GRID_STYLES.border}`)}>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-10 w-10" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-48" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-16" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-32" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-8" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-16" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-28" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-4 w-24" /></td>
                <td className={GRID_STYLES.cell.base}><Skeleton className="h-8 w-20" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-8 text-center">
        <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-4 text-lg font-medium text-red-800">Failed to load assets</h3>
        <p className="mt-2 text-sm text-red-600">{error}</p>
        <Button onClick={onRetry} variant="outline" className="mt-4">
          Try Again
        </Button>
      </div>
    );
  }

  // Empty state
  if (assets.length === 0) {
    return (
      <div className="rounded-md border border-[#E8E8E8] bg-gray-50 p-8 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
          <Image className="h-6 w-6 text-gray-400" />
        </div>
        <h3 className="mt-4 text-lg font-medium text-gray-900">No assets found</h3>
        <p className="mt-2 text-sm text-gray-500">
          Upload your first asset to get started.
        </p>
      </div>
    );
  }

  // Data state
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-[#E8E8E8] overflow-hidden">
        <table className="w-full">
          <thead className={GRID_STYLES.header.background}>
            <tr>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-16')}></th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>Name</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Type</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-40')}>Application</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-20')}>Version</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-24')}>Size</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-40')}>Modified</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-32')}>Modified By</th>
              <th className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text, 'w-32')}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {assets.map((asset) => (
              <tr
                key={asset.assetId}
                className={cn(
                  GRID_STYLES.row.default,
                  GRID_STYLES.row.hover,
                  `border-b ${GRID_STYLES.border}`,
                  'transition-colors cursor-pointer'
                )}
                onDoubleClick={() => onView(asset)}
              >
                <td className={GRID_STYLES.cell.base}>
                  <AssetThumbnail asset={asset} />
                </td>
                <td className={GRID_STYLES.cell.base}>
                  <div className="flex flex-col">
                    <span className="font-medium truncate" title={asset.name}>
                      {asset.name}
                    </span>
                    {asset.description && (
                      <span className="text-xs text-gray-500 truncate" title={asset.description}>
                        {asset.description}
                      </span>
                    )}
                  </div>
                </td>
                <td className={cn(GRID_STYLES.cell.base, 'text-sm')}>
                  <div className="flex items-center gap-1.5">
                    {getAssetTypeIcon(asset.assetType)}
                    <span>{ASSET_TYPE_LABELS[asset.assetType]}</span>
                  </div>
                </td>
                <td className={cn(GRID_STYLES.cell.base, 'text-sm text-gray-600')}>
                  {APPLICATION_TYPE_LABELS[asset.applicationType]}
                </td>
                <td className={cn(GRID_STYLES.cell.base, 'text-sm')}>
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                    v{asset.currentVersion}
                  </span>
                </td>
                <td className={cn(GRID_STYLES.cell.base, 'text-sm text-gray-600')}>
                  {formatFileSize(asset.fileSizeBytes)}
                </td>
                <td className={cn(GRID_STYLES.cell.base, 'text-sm text-gray-600')}>
                  {formatDate(asset.modified)}
                </td>
                <td className={cn(GRID_STYLES.cell.base, 'text-sm text-gray-600 truncate')} title={asset.modifiedByName || undefined}>
                  {asset.modifiedByName || '-'}
                </td>
                <td className={GRID_STYLES.cell.base}>
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(asset.currentS3Url, 'URL copied to clipboard')}
                      className="h-8 w-8 p-0 text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                      title="Copy file URL"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(asset.currentS3Url, '_blank')}
                      className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title="Open asset in new tab"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onView(asset)}
                      className="h-8 w-8 p-0"
                      title="View details"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(asset)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete asset"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalCount > pageSize && (
        <div className={cn('flex items-center justify-between px-2 py-3 border-x border-b bg-white', GRID_STYLES.border)}>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(0)} disabled={currentPage === 0}>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getPageNumbers().map((pageNum) => (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? 'default' : 'ghost'}
                size="sm"
                className={`h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626]' : 'text-[#262626] hover:bg-[#FFFBEB]'}`}
                onClick={() => onPageChange(pageNum)}
              >
                {pageNum + 1}
              </Button>
            ))}
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1}>
              <ChevronsRight className="h-4 w-4" />
            </Button>
            <span className="text-sm text-[#606060] ml-2">{currentPage + 1} of {totalPages} pages</span>
          </div>
          <span className="text-sm text-[#606060]">{startItem}-{endItem} of {totalCount.toLocaleString()}</span>
        </div>
      )}
    </div>
  );
}
